import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

export type DashboardEventType = 'referent' | 'sci-referent' | 'boitage' | 'followup' | 'newsletter';

export interface DashboardEventPositions {
  referent: { [key: string]: number };
  'sci-referent': { [key: string]: number };
  boitage: { [key: string]: number };
  newsletter?: { [key: string]: { position: number; theme: string } };
  followup?: { [key: string]: number };
}

export interface DashboardEventActivation {
  referent: { [key: string]: boolean };
  'sci-referent': { [key: string]: boolean };
  boitage: { [key: string]: boolean };
}

export interface DashboardPersonAssignments {
  [rowKey: string]: number | null;
}

export interface DashboardExactDates {
  [eventKey: string]: { day: number; month: number };
}

export interface DashboardState {
  planningActivated: boolean;
  modulesActivated: boolean;
  timelineLocked: boolean;
  eventPositions: DashboardEventPositions;
  eventActivation: DashboardEventActivation;
  personAssignments: DashboardPersonAssignments;
  exactDates: DashboardExactDates;
}

const initialEventPositions: DashboardEventPositions = {
    referent: { 'Référent 1': 1.5, 'Référent 2': 7.5, 'Référent 3': 11 },
    'sci-referent': { 'SCI 1': 0.5, 'SCI 2': 6.5, 'SCI 3': 10.5 },
    boitage: { 'Boîtage 1': 2.5, 'Boîtage 2': 8.5 },
  newsletter: {
    'Newsletter 1': { position: 0, theme: 'DPE' },
    'Newsletter 2': { position: 3, theme: 'Vivre ensemble' },
    'Newsletter 3': { position: 6, theme: 'Travaux' },
    'Newsletter 4': { position: 9, theme: 'Vente' },
  },
  followup: { 'Point 1': 3, 'Point 2': 7 },
};

const initialEventActivation: DashboardEventActivation = {
  referent: { 'Référent 1': true, 'Référent 2': true, 'Référent 3': false },
  'sci-referent': { 'SCI 1': true, 'SCI 2': true, 'SCI 3': false },
  boitage: { 'Boîtage 1': true, 'Boîtage 2': true },
};

const initialState: DashboardState = {
  planningActivated: true,
  modulesActivated: true,
  timelineLocked: true,
  eventPositions: initialEventPositions,
  eventActivation: initialEventActivation,
  personAssignments: {},
  exactDates: {},
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    togglePlanning: (state) => {
      state.planningActivated = !state.planningActivated;
    },
    toggleModules: (state) => {
      state.modulesActivated = !state.modulesActivated;
    },
    updateEventPosition: (state, action: PayloadAction<{ type: DashboardEventType; eventText: string; position: number }>) => {
      const { type, eventText, position } = action.payload;
      if (type === 'newsletter') {
        if (state.eventPositions.newsletter && state.eventPositions.newsletter[eventText]) {
          state.eventPositions.newsletter[eventText].position = position;
        }
      } else if (type === 'followup') {
        if (!state.eventPositions.followup) {
          state.eventPositions.followup = {};
        }
        state.eventPositions.followup[eventText] = position;
      } else {
        state.eventPositions[type][eventText] = position;
      }
    },
    updateEventActivation: (state, action: PayloadAction<{ type: DashboardEventType; eventText: string; active: boolean }>) => {
      const { type, eventText, active } = action.payload;
      state.eventActivation[type][eventText] = active;
    },
    updateNewsletterTheme: (state, action: PayloadAction<{ eventText: string; theme: string }>) => {
      const { eventText, theme } = action.payload;
      if (state.eventPositions.newsletter && state.eventPositions.newsletter[eventText]) {
        state.eventPositions.newsletter[eventText].theme = theme;
      }
    },
    toggleTimelineLock: (state) => {
      state.timelineLocked = !state.timelineLocked;
    },
    updatePersonAssignment: (state, action: PayloadAction<{ rowKey: string; userId: number | null }>) => {
      const { rowKey, userId } = action.payload;
      state.personAssignments[rowKey] = userId;
    },
    updateExactDate: (state, action: PayloadAction<{ eventKey: string; date: { day: number; month: number } | null }>) => {
      const { eventKey, date } = action.payload;
      if (date) {
        state.exactDates[eventKey] = date;
      } else {
        delete state.exactDates[eventKey];
      }
    },
    loadTimelineData: (state, action: PayloadAction<{ eventPositions?: DashboardEventPositions; eventActivation?: DashboardEventActivation; personAssignments?: DashboardPersonAssignments; exactDates?: DashboardExactDates }>) => {
      const { eventPositions, eventActivation, personAssignments, exactDates } = action.payload;

      if (eventPositions) {
        state.eventPositions = {
          referent: {
            ...state.eventPositions.referent,
            ...(eventPositions.referent ?? {}),
          },
          'sci-referent': {
            ...state.eventPositions['sci-referent'],
            ...(eventPositions['sci-referent'] ?? {}),
          },
          boitage: {
            ...state.eventPositions.boitage,
            ...(eventPositions.boitage ?? {}),
          },
          newsletter: eventPositions.newsletter
            ? Object.fromEntries(
                Object.entries({
                  ...(state.eventPositions.newsletter ?? {}),
                  ...eventPositions.newsletter,
                }).map(([key, val]) => [
                  key,
                  { ...val, theme: val.theme === 'Règlement' ? 'Vivre ensemble' : val.theme },
                ]),
              )
            : state.eventPositions.newsletter,
          followup: eventPositions.followup
            ? {
                ...(state.eventPositions.followup ?? {}),
                ...eventPositions.followup,
              }
            : state.eventPositions.followup,
        };
      }

      if (eventActivation) {
        state.eventActivation = {
          referent: {
            ...state.eventActivation.referent,
            ...(eventActivation.referent ?? {}),
          },
          'sci-referent': {
            ...state.eventActivation['sci-referent'],
            ...(eventActivation['sci-referent'] ?? {}),
          },
          boitage: {
            ...state.eventActivation.boitage,
            ...(eventActivation.boitage ?? {}),
          },
        };
      }

      if (personAssignments) {
        state.personAssignments = { ...state.personAssignments, ...personAssignments };
      }

      if (exactDates) {
        state.exactDates = { ...state.exactDates, ...exactDates };
      }
    },
    resetTimelineToDefaults: (state) => {
      state.eventPositions = initialEventPositions;
      state.eventActivation = initialEventActivation;
      state.personAssignments = {};
      state.exactDates = {};
    },
  },
});

export const {
  togglePlanning,
  toggleModules,
  updateEventPosition,
  updateEventActivation,
  updateNewsletterTheme,
  toggleTimelineLock,
  updatePersonAssignment,
  updateExactDate,
  loadTimelineData,
  resetTimelineToDefaults,
} = dashboardSlice.actions;

export default dashboardSlice.reducer; 